import { useTranslation } from 'react-i18next';
import { VStack, HStack, Box, Pressable } from 'native-base';
import { flatMap, isEmpty, snakeCase } from 'lodash';

import { Text } from '@pimm/base';
import { Dispenser } from '@pimm/services/lib/coke-freestyle';
import { ResourceLoader } from '@app/components/shared';
import { useSiteConfig } from '@app/features/store-core';
import { dispenserName, useGetSiteDispensers } from '@app/features/coke-freestyle';
import { FreestyleLogo } from '@app/features/coke-freestyle/assets';
import { WidgetBox, WidgetBoxProps } from '../widget-box';

type WidgetCokeFreeStyleProps = WidgetBoxProps & {
  onPressDispenser?: (dispenser: Dispenser) => void;
};

export const WidgetCokeFreeStyle = ({ onPressDispenser, ...props }: WidgetCokeFreeStyleProps) => {
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const cokeFreeStyle = siteConfig?.config?.dashboardConfig?.cokeFreeStyle;
  const siteDispensers = useGetSiteDispensers(cokeFreeStyle?.enabled ? siteConfig?.id : undefined);

  const handlePressDispenser = (dispenser: Dispenser) => () => {
    if (onPressDispenser) onPressDispenser(dispenser);
  };

  const dispensers = flatMap(siteDispensers?.data ?? [], _ => _.dispensers ?? []);

  return (
    <WidgetBox {...props}>
      <ResourceLoader
        minH="90px"
        flex={1}
        isEmpty={isEmpty(dispensers)}
        isLoading={siteDispensers?.isLoading ?? false}
        _text={{ size: 'sm' }}
      >
        <HStack space={3} w="full" h="full" alignItems="center">
          <FreestyleLogo width={27} height={52} color="red" />
          <VStack space={1} flex={1} rounded="md">
            {dispensers.map(_ => (
              <Pressable key={_.dispenserId} rounded="md" onPress={handlePressDispenser(_)}>
                <HStack space={2} flex={1} rounded="md" alignItems="center" p={1} pl={2} minH={7} backgroundColor="gray.50">
                  <Box flex={1}>
                    <Text size="md" color="black" fontWeight={600} numberOfLines={2} lineHeight="xs">
                      {_.displayName || t(`coke_freesstyle:${snakeCase(dispenserName(_.type).toLowerCase())}`)}
                    </Text>
                  </Box>

                  <HStack space={1} alignItems="center" justifyContent="space-between">
                    <Box
                      flex="none"
                      rounded="md"
                      alignItems="center"
                      justifyContent="center"
                      px={1}
                      h={4}
                      bgColor={_?.lastUpdated ? 'success.50' : 'error.50'}
                    >
                      <Text size="xs" color={_?.lastUpdated ? 'success.700' : 'error.700'} fontWeight={500} lineHeight="xs">
                        {_?.lastUpdated ? t('widgets:online') : t('widgets:offline')}
                      </Text>
                    </Box>

                    <Box justifyContent="center" minW={8} h={5} rounded="md" bgColor="error.500" opacity={_.emptyCount ? 1 : 0.3}>
                      <Text size="lg" fontWeight={700} color="white" textAlign="center">
                        {_.emptyCount ?? 0}
                      </Text>
                    </Box>

                    <Box justifyContent="center" minW={8} h={5} rounded="md" bgColor="warning.400" opacity={_.lowCount ? 1 : 0.3}>
                      <Text size="lg" fontWeight={700} color="white" textAlign="center">
                        {_.lowCount ?? 0}
                      </Text>
                    </Box>
                  </HStack>
                </HStack>
              </Pressable>
            ))}
          </VStack>
        </HStack>
      </ResourceLoader>
    </WidgetBox>
  );
};
